import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { motion } from 'framer-motion';
import { Compass, Filter, MapPinned, ShieldCheck, Eye, EyeOff, Lock } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABEL = {
  pending_ai_review: 'Pending AI Review',
  high_confidence: 'High Confidence',
  community_verified: 'Community Verified',
  expert_verified: 'Expert Verified',
  research_verified: 'Research Grade',
  rejected: 'Rejected',
  archived: 'Archived',
};

const STATUS_COLOR = {
  pending_ai_review: '#9CA3AF',
  high_confidence: '#E9C46A',
  community_verified: '#95D5B2',
  expert_verified: '#40916C',
  research_verified: '#2A9D8F',
  rejected: '#EF4444',
  archived: '#6B7280',
};

const PRIVACY_LABEL = {
  public: 'Public', obscured: 'Obscured', private: 'Private', research_only: 'Research',
};

const PRIVACY_ICON = { public: Eye, obscured: EyeOff, private: Lock, research_only: ShieldCheck };

export default function Observations() {
  const nav = useNavigate();
  const [obs, setObs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [privacyFilter, setPrivacyFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (privacyFilter !== 'all') params.set('privacy', privacyFilter);
      const { data } = await api.get(`/observations/me?${params.toString()}`);
      setObs(data.observations || []);
    } catch {
      toast.error('Failed to load observations');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter, privacyFilter]); // eslint-disable-line

  return (
    <div className="min-h-screen bg-[#04120c] text-foreground pb-24" data-testid="observations-root">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Compass className="w-4 h-4 text-primary-light" />
            <h1 className="font-serif text-2xl">My Observations</h1>
          </div>
          <button
            data-testid="btn-open-biodiversity-map"
            onClick={() => nav('/biodiversity')}
            className="px-3 py-2 rounded-full glass border border-white/10 text-xs flex items-center gap-1 hover:bg-white/10"
          >
            <MapPinned className="w-3.5 h-3.5" /> Network Map
          </button>
        </div>

        {/* filters */}
        <div className="flex flex-col gap-2 mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-3.5 h-3.5 text-white/40" />
            {['all', 'pending_ai_review', 'high_confidence', 'community_verified', 'expert_verified', 'research_verified'].map(s => (
              <button
                key={s}
                data-testid={`obs-filter-status-${s}`}
                onClick={() => setStatusFilter(s)}
                className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider border ${
                  statusFilter === s ? 'bg-primary text-white border-primary' : 'glass border-white/10 text-white/70'
                }`}
              >{s === 'all' ? 'All Statuses' : STATUS_LABEL[s]}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Lock className="w-3.5 h-3.5 text-white/40" />
            {['all', 'public', 'obscured', 'private', 'research_only'].map(p => (
              <button
                key={p}
                data-testid={`obs-filter-privacy-${p}`}
                onClick={() => setPrivacyFilter(p)}
                className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider border ${
                  privacyFilter === p ? 'bg-primary text-white border-primary' : 'glass border-white/10 text-white/70'
                }`}
              >{p === 'all' ? 'All Privacy' : PRIVACY_LABEL[p]}</button>
            ))}
          </div>
        </div>

        {loading && <p className="text-sm text-white/60 py-8 text-center">Loading…</p>}

        {!loading && obs.length === 0 && (
          <div className="p-8 rounded-3xl glass border border-white/10 text-center" data-testid="obs-empty">
            <p className="text-white/70">No observations yet.</p>
            <button
              onClick={() => nav('/discover')}
              className="mt-3 px-4 py-2 rounded-full bg-primary text-white text-sm"
            >Discover your first species</button>
          </div>
        )}

        <ul className="space-y-2">
          {obs.map(o => {
            const status = o.verification_status || 'pending_ai_review';
            const privacy = o.privacy || 'private';
            const PIcon = PRIVACY_ICON[privacy] || Lock;
            return (
              <motion.li
                key={o.observation_id}
                data-testid={`obs-row-${o.observation_id}`}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                onClick={() => nav(`/observations/${o.observation_id}`)}
                className="p-3 rounded-2xl glass border border-white/10 flex items-center gap-3 cursor-pointer hover:bg-white/5"
              >
                {o.image_path ? (
                  <img
                    src={`${api.defaults.baseURL}/files/${o.image_path}`}
                    alt=""
                    className="w-14 h-14 rounded-xl object-cover border border-white/10"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-white/5 flex items-center justify-center">
                    <Compass className="w-5 h-5 text-white/30" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{o.species_common_name}</p>
                  <p className="italic text-xs text-white/60 truncate">{o.species_scientific_name}</p>
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    <span
                      className="px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest"
                      style={{ background: `${STATUS_COLOR[status]}22`, color: STATUS_COLOR[status] }}
                    >{STATUS_LABEL[status]}</span>
                    <span className="px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest bg-white/10 text-white/70 flex items-center gap-1">
                      <PIcon className="w-2.5 h-2.5" /> {PRIVACY_LABEL[privacy]}
                    </span>
                    {o.is_sensitive && (
                      <span className="px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest bg-red-500/20 text-red-300">Sensitive</span>
                    )}
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-white/40">
                  {o.discovery_score ?? 0} pts
                </span>
              </motion.li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
